import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/toast";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { Button } from "../components/button";
import { ErrorMessage } from "../components/error-message";
import { EditableField } from "../components/editable-field";
import { UsernameField } from "../components/username-field";
type RegisterFormProps = {
  route?: {
    pathname?: string;
    search?: string;
  };
};

export const SignUpForm = ({ route = {} }: RegisterFormProps) => {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    confirmPassword: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { error, handleError, clearError } = useErrorHandler();
  const { register } = useAuth();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [usernameApiError, setUsernameApiError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLElement>) => {
    const { name, value } = e.target as HTMLInputElement;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) clearError();
    setUsernameApiError(null);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    clearError();
    setUsernameApiError(null);

    const { username, password, confirmPassword } = formData;
    const trimmedUsername = username.trim();
    const trimmedPassword = password.trim();
    const trimmedConfirmPassword = confirmPassword.trim();

    if (!trimmedUsername || !trimmedPassword || !trimmedConfirmPassword) {
      handleError("Por favor, completa todos los campos.");
      setIsSubmitting(false);
      return;
    }

    if (trimmedPassword !== trimmedConfirmPassword) {
      handleError("Las contraseñas no coinciden.");
      setIsSubmitting(false);
      return;
    }

    if (trimmedPassword.length < 6) {
      handleError("La contraseña debe tener al menos 6 caracteres.");
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await register(trimmedUsername, trimmedPassword);
      console.log("response", response.additionalData);
      console.log("additionalData", response?.data?.additionalData);
      if (response.statusCode === 201) {
        addToast({
          type: "success",
          message: "¡Registro exitoso!",
          location: "bottom-center",
        });
        setTimeout(() => {
          navigate("/dashboard", { replace: true });
        }, 100);
      } else {
        if (response.additionalData?.errorCode === "USERNAME_IN_USE") {
          setUsernameApiError("Este nombre de usuario ya está en uso.");
        }
        handleError(response.message || "Registro fallido.");
      }
    } catch (error) {
      handleError("Hubo un problema con el registro.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <form onSubmit={handleRegister} className="space-y-4">
      <UsernameField
        label="Nombre de Usuario"
        name="username"
        id="username"
        required
        value={formData.username}
        onChange={handleChange}
        minLength={6}
        maxLength={35}
        placeholder="Ingresa tu nombre de usuario"
        autocomplete="off"
        customError={usernameApiError ?? ""}
        apiError={usernameApiError !== null ? "conflict" : undefined}
        title="Solo letras, números, guiones bajos (_) y puntos (.)"
      />

      <EditableField
        type="password"
        label="Contraseña"
        name="password"
        id="password"
        required
        value={formData.password}
        onChange={handleChange}
        minLength={6}
        maxLength={35}
        title="La contraseña debe tener al menos 6 caracteres y no más de 35."
        placeholder="Ingresa tu contraseña"
        autocomplete="off"
      />

      <EditableField
        label="Confirmar Contraseña"
        type="password"
        name="confirmPassword"
        id="confirm-password"
        required
        value={formData.confirmPassword}
        onChange={handleChange}
        minLength={6}
        maxLength={35}
        title="La contraseña debe tener al menos 6 caracteres y no más de 35."
        placeholder="Ingresa tu contraseña de nuevo"
        autocomplete="off"
      />

      {error && <ErrorMessage message={error} dismissible />}

      <Button
        type="submit"
        text="Registrarse"
        disabled={isSubmitting || error !== null || usernameApiError !== null}
        variant="auth"
      />
    </form>
  );
};
